import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePhoneNumber } from '../context/PhoneContext';
import { FileText, Music, Download, Crown, Calendar, Phone } from 'lucide-react';
import './PortalPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PortalPage() {
  const [subscription, setSubscription] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const phone = usePhoneNumber();

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/portal');
      return;
    }
    fetchPortalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchPortalData = async () => {
    try {
      const [subRes, dlRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/portal/downloads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSubscription(subRes.data);
      setDownloads(dlRes.data.downloads || []);
    } catch (error) {
      console.error('Error fetching portal data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="portal-page" data-testid="portal-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="portal-page" data-testid="portal-page">
      <div className="portal-header">
        <div className="container">
          <div className="header-content">
            <div className="welcome-text">
              <h1>Welcome{user?.name ? `, ${user.name}` : ''}</h1>
              <p>Your Binding Thread Portal</p>
            </div>
            {subscription?.is_founding && (
              <div className="founding-badge" data-testid="founding-member-badge">
                <Crown size={18} />
                <span>Founding Member</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="portal-content">
        <div className="container">
          {!subscription?.has_subscription ? (
            <div className="no-subscription" data-testid="no-subscription">
              <h2>No Active Subscription</h2>
              <p>Start your journey with The Living Letter subscription.</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/shop')}
                data-testid="browse-shop-btn"
              >
                Browse Shop
              </button>
            </div>
          ) : !subscription?.onboarding_complete ? (
            <div className="onboarding-required" data-testid="onboarding-required">
              <h2>Complete Your Setup</h2>
              <p>Please complete your onboarding to start receiving letters.</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/onboarding')}
                data-testid="complete-onboarding-btn"
              >
                Complete Onboarding
              </button>
            </div>
          ) : (
            <div className="portal-grid">
              {/* Subscription Info */}
              <section className="portal-card subscription-card" data-testid="subscription-info">
                <h3>Your Subscription</h3>
                <div className="subscription-details">
                  <div className="detail-item">
                    <Calendar size={18} />
                    <span>Status: <strong>{subscription.status}</strong></span>
                  </div>
                  <div className="detail-item">
                    <Phone size={18} />
                    <span>Parent: <strong>{subscription.parent_name}</strong></span>
                  </div>
                </div>
              </section>

              {/* Downloads */}
              <section className="portal-card downloads-card" data-testid="downloads-section">
                <h3>Your Letters & Audio</h3>
                <p className="card-description">
                  Access digital backups of your keepsake letters and audio recordings.
                </p>

                {downloads.length > 0 ? (
                  <div className="downloads-list">
                    {downloads.map((download, index) => (
                      <div key={index} className="download-item" data-testid={`download-${index}`}>
                        <div className="download-info">
                          <span className="download-month">{download.month}</span>
                          <span className="download-title">{download.title}</span>
                        </div>
                        <div className="download-actions">
                          {download.mp3_url && (
                            <a
                              href={download.mp3_url}
                              className="download-btn"
                              title="Download Audio"
                              data-testid={`download-mp3-${index}`}
                            >
                              <Music size={16} />
                              <span>MP3</span>
                            </a>
                          )}
                          {download.pdf_url && (
                            <a
                              href={download.pdf_url}
                              className="download-btn"
                              title="Download PDF"
                              data-testid={`download-pdf-${index}`}
                            >
                              <FileText size={16} />
                              <span>PDF</span>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-downloads" data-testid="no-downloads">
                    <Download size={32} />
                    <p>Your first letter is on its way!</p>
                    <span>We&apos;ve sent an SMS to your loved one letting them know they&apos;ll receive a welcome pack this week with all the information they need to call {phone.display}.</span>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PortalPage;
